import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Clock, TrendingUp, BarChart3, Loader2, RefreshCw, Table2, Info } from 'lucide-react';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DEFAULT_HOURS = [12, 13, 14, 15, 20, 21, 22, 23];

function HeatCell({ value }) {
  if (!value || value === 0) return <div className="w-full h-full rounded bg-slate-50 border border-slate-100" />;
  const intensity = value / 120;
  const bg = `rgba(249,115,22,${Math.min(0.9, intensity)})`;
  return (
    <div className="w-full h-full rounded flex items-center justify-center text-[10px] font-bold transition-all hover:scale-110 cursor-default"
      style={{ backgroundColor: bg, color: intensity > 0.4 ? '#fff' : '#c2410c' }}
      title={`${value} min promedio`}>
      {value}m
    </div>
  );
}

export default function TableTurnTime() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/analytics/turn-times');
      setData(r.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Tiempo de Rotación" helpSection="analytics" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  const avgMin = data?.avg_minutes || 0;
  const tracked = data?.total_tracked || 0;
  const heatmap = Array.isArray(data?.heatmap) ? data.heatmap : [];
  const bySize = Array.isArray(data?.by_size) ? data.by_size : [];
  const hours = Array.isArray(data?.hours) ? data.hours : DEFAULT_HOURS;
  const hasData = tracked > 0;

  const formatMin = (m) => {
    if (!m || m === 0) return '—';
    return `${Math.floor(m / 60) > 0 ? `${Math.floor(m / 60)}h ` : ''}${m % 60}min`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Tiempo de Rotación de Mesas" helpSection="analytics" actions={
        <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
          <RefreshCw className="h-3.5 w-3.5" /> Actualizar
        </button>
      } />

      <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-indigo-500 flex items-center justify-center">
            <Clock className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Tiempo de Rotación</h1>
            <p className="text-sm text-slate-500">Analiza cuánto tiempo permanecen los comensales en mesa y optimiza tu servicio</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { l: 'Tiempo medio', v: hasData ? formatMin(avgMin) : '—', icon: Clock, c: 'text-indigo-600', bg: 'bg-indigo-50' },
            { l: 'Servicio rápido', v: '< 60 min', icon: TrendingUp, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Servicio estándar', v: '60–90 min', icon: Table2, c: 'text-amber-600', bg: 'bg-amber-50' },
            { l: 'Reservas analizadas', v: tracked, icon: BarChart3, c: 'text-slate-600', bg: 'bg-slate-100' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-4 w-4", s.c)} />
                </div>
                <div className="text-2xl font-bold text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info banner */}
        {!hasData && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3 text-sm text-blue-700">
            <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="block mb-1">Sin datos de rotación todavía</strong>
              Para ver estadísticas reales, el equipo debe registrar el tiempo de llegada (<code>seated_at</code>) y salida (<code>left_at</code>) en cada mesa desde el Libro Digital. Una vez haya reservas completadas con esos datos, el heatmap y los gráficos se activarán automáticamente.
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          {/* By party size */}
          <Card className="border-0 shadow-sm col-span-1">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Por tamaño de mesa</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!hasData ? (
                <div className="text-center py-8 text-slate-400">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Sin datos aún</p>
                </div>
              ) : bySize.map(s => (
                <div key={s.size}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 font-medium">{s.size} personas</span>
                    <span className="font-bold text-slate-800">{formatMin(s.avg)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-indigo-400" style={{ width: `${Math.min(100, (s.avg / 120) * 100)}%` }} />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">{s.count} reservas</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Heatmap */}
          <Card className="border-0 shadow-sm col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Heatmap por día y hora (minutos)
                <div className="flex items-center gap-1 text-[10px] text-slate-400 font-normal">
                  <span>Bajo</span>
                  <div className="flex gap-0.5">
                    {[0.1,0.3,0.5,0.7,0.9].map(i => <div key={i} className="w-4 h-3 rounded-sm" style={{ backgroundColor: `rgba(249,115,22,${i})` }} />)}
                  </div>
                  <span>Alto</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!hasData ? (
                <div className="h-48 flex flex-col items-center justify-center text-slate-400">
                  <Clock className="h-10 w-10 mb-2 opacity-20" />
                  <p className="text-sm font-medium">Heatmap disponible con datos reales</p>
                  <p className="text-xs mt-1">Registra llegada y salida en el Libro Digital</p>
                </div>
              ) : (
                <div className="flex gap-1">
                  <div className="flex flex-col justify-between py-1 mr-1">
                    {DAYS.map(d => <span key={d} className="text-[10px] text-slate-400 h-7 flex items-center">{d}</span>)}
                  </div>
                  <div className="flex-1">
                    <div className="grid gap-0.5 mb-1" style={{ gridTemplateColumns: `repeat(${hours.length}, minmax(0, 1fr))` }}>
                      {hours.map(h => <span key={h} className="text-[9px] text-slate-400 text-center">{h}h</span>)}
                    </div>
                    {heatmap.map((row, di) => (
                      <div key={di} className="grid gap-0.5 mb-0.5" style={{ gridTemplateColumns: `repeat(${hours.length}, minmax(0, 1fr))` }}>
                        {(Array.isArray(row) ? row : []).map((val, hi) => (
                          <div key={hi} className="h-7">
                            <HeatCell value={val} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tips */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Consejos para optimizar la rotación</h3>
            <div className="grid grid-cols-3 gap-4 text-xs text-slate-600">
              {[
                { t: 'Marcar tiempo de llegada', d: 'Registra cuando los comensales se sientan para tener datos precisos de rotación.' },
                { t: 'Alertas por tiempo', d: 'Configura alertas cuando una mesa supere el tiempo medio estimado.' },
                { t: 'Reservas escalonadas', d: 'Distribuye las reservas en intervalos de 15-30 minutos para evitar cuellos de botella.' },
              ].map(tip => (
                <div key={tip.t} className="p-3 bg-orange-50 rounded-xl">
                  <p className="font-semibold text-orange-700 mb-1">{tip.t}</p>
                  <p className="text-slate-500">{tip.d}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
